import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { AuthHTTPService } from './auth-http/auth-http.service';

export interface PasskeyCredential {
  id: string;
  type: string;
  rawId: ArrayBuffer;
  response: {
    attestationObject?: ArrayBuffer;
    clientDataJSON: ArrayBuffer;
    authenticatorData?: ArrayBuffer;
    signature?: ArrayBuffer;
    userHandle?: ArrayBuffer;
  };
}

export interface PasskeyRegistrationOptions {
  challenge: string;
  rp: {
    name: string;
    id: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: Array<{
    type: string;
    alg: number;
  }>;
  timeout: number;
  attestation: string;
  authenticatorSelection?: {
    authenticatorAttachment?: string;
    userVerification?: string;
    requireResidentKey?: boolean;
  };
}

export interface PasskeyAuthenticationOptions {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials?: Array<{
    type: string;
    id: string;
    transports?: string[];
  }>;
  userVerification?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PasskeyService {

  constructor(private authHttpService: AuthHTTPService) {}

  /**
   * Check if passkeys are supported by the browser
   */
  isSupported(): boolean {
    return !!(navigator.credentials && 
              typeof navigator.credentials.create === 'function' && 
              typeof navigator.credentials.get === 'function');
  }

  /**
   * Register a new passkey (requires authentication)
   */
  registerPasskey(name?: string): Observable<any> {
    if (!this.isSupported()) {
      return throwError(() => new Error('Passkeys are not supported in this browser'));
    }

    console.log('[PasskeyService] Starting passkey registration with name:', name);
    
    return this.authHttpService.startPasskeyRegistration().pipe(
      tap(options => {
        console.log('[PasskeyService] Received registration options from server:', options);
        console.log('[PasskeyService] Registration options type:', typeof options);
        console.log('[PasskeyService] Registration options keys:', options ? Object.keys(options) : 'null/undefined');
        console.log('[PasskeyService] Registration options structure:', JSON.stringify(options, null, 2));
      }),
      switchMap(options => {
        console.log('[PasskeyService] Converting registration options...');
        const regOptions = this.convertRegistrationOptions(options);
        console.log('[PasskeyService] Converted WebAuthn options:', regOptions);
        
        return from(this.createCredential(regOptions));
      }),
      switchMap(credential => {
        console.log('[PasskeyService] Got credential, sending to server...');
        const convertedCredential = this.convertCredentialForServer(credential as PublicKeyCredential);
        return this.authHttpService.completePasskeyRegistration(convertedCredential, name);
      }),
      catchError(error => {
        console.error('[PasskeyService] Passkey registration error:', error);
        console.error('[PasskeyService] Error name:', error?.name);
        console.error('[PasskeyService] Error message:', error?.message);
        console.error('[PasskeyService] Error stack:', error?.stack);
        return throwError(() => error);
      })
    );
  }

  /**
   * Authenticate with a passkey (for sign-in - unauthenticated)
   */
  authenticateWithPasskey(): Observable<any> {
    if (!this.isSupported()) {
      return throwError(() => new Error('Passkeys are not supported in this browser'));
    }

    console.log('[PasskeyService] Starting passkey authentication for sign-in...');
    
    return this.authHttpService.startPasskeyAuthentication().pipe(
      tap(options => {
        console.log('[PasskeyService] Received options from server:', options);
        console.log('[PasskeyService] Options type:', typeof options);
        console.log('[PasskeyService] Options keys:', options ? Object.keys(options) : 'null/undefined');
      }),
      switchMap(options => {
        console.log('[PasskeyService] Converting authentication options...');
        const authOptions = this.convertAuthenticationOptions(options);
        console.log('[PasskeyService] Converted options:', authOptions);
        return from(this.getCredential(authOptions));
      }),
      switchMap(credential => {
        console.log('[PasskeyService] Got credential, converting for server...');
        const convertedCredential = this.convertCredentialForServer(credential);
        console.log('[PasskeyService] Sending credential to server...');
        return this.authHttpService.completePasskeyAuthentication(convertedCredential);
      }),
      catchError(error => {
        console.error('[PasskeyService] Passkey authentication error:', error);
        console.error('[PasskeyService] Error stack:', (error as any)?.stack);
        const friendly = this.mapAuthenticationError(error);
        return throwError(() => friendly);
      })
    );
  }

  /**
   * Authenticate with a passkey (for authenticated users - e.g., MFA)
   */
  authenticateWithPasskeyAuthenticated(): Observable<any> {
    if (!this.isSupported()) {
      return throwError(() => new Error('Passkeys are not supported in this browser'));
    }

    console.log('[PasskeyService] Starting authenticated passkey authentication...');
    
    return this.authHttpService.startPasskeyAuthenticationAuthenticated().pipe(
      tap(options => {
        console.log('[PasskeyService] Received options from server:', options);
        console.log('[PasskeyService] Options type:', typeof options);
        console.log('[PasskeyService] Options keys:', options ? Object.keys(options) : 'null/undefined');
      }),
      switchMap(options => {
        console.log('[PasskeyService] Converting authentication options...');
        const authOptions = this.convertAuthenticationOptions(options);
        console.log('[PasskeyService] Converted options:', authOptions);
        return from(this.getCredential(authOptions));
      }),
      switchMap(credential => {
        console.log('[PasskeyService] Got credential, converting for server...');
        const convertedCredential = this.convertCredentialForServer(credential);
        console.log('[PasskeyService] Sending credential to server...');
        return this.authHttpService.completePasskeyAuthenticationAuthenticated(convertedCredential);
      }),
      catchError(error => {
        console.error('[PasskeyService] Passkey authentication error:', error);
        console.error('[PasskeyService] Error stack:', error.stack);
        return throwError(() => error);
      })
    );
  }

  /**
   * List user's passkeys
   */
  listPasskeys(): Observable<any[]> {
    return this.authHttpService.listPasskeys();
  }

  /**
   * Delete a passkey
   */
  deletePasskey(passkeyId: string): Observable<boolean> {
    return this.authHttpService.deletePasskey(passkeyId);
  }

  /**
   * Rename a passkey
   */
  renamePasskey(passkeyId: string, newName: string): Observable<boolean> {
    console.log('[PasskeyService] Renaming passkey:', { passkeyId, newName });
    return this.authHttpService.renamePasskey(passkeyId, newName);
  }

  /**
   * Convert server registration options to WebAuthn format
   */
  private convertRegistrationOptions(serverOptions: any): CredentialCreationOptions {
    if (!serverOptions) {
      throw new Error('Invalid server options: missing options object');
    }

    // Handle nested challenge structure (like authentication endpoint)
    let challengeData = serverOptions;
    if (serverOptions.challenge && typeof serverOptions.challenge === 'object') {
      challengeData = serverOptions.challenge;
    }

    if (!challengeData.challenge) {
      throw new Error('Invalid server options: missing challenge');
    }
    
    if (!challengeData.user || !challengeData.user.id) {
      throw new Error('Invalid server options: missing user information');
    }
    
    console.log('[PasskeyService] Converting challenge:', challengeData.challenge);
    console.log('[PasskeyService] Converting user.id:', challengeData.user.id);
    
    const challengeBuffer = this.base64ToArrayBuffer(challengeData.challenge);
    const userIdBuffer = this.base64ToArrayBuffer(challengeData.user.id);
    
    console.log('[PasskeyService] Challenge buffer length:', challengeBuffer.byteLength);
    console.log('[PasskeyService] User ID buffer length:', userIdBuffer.byteLength);
    
    return {
      publicKey: {
        challenge: challengeBuffer,
        rp: challengeData.rp,
        user: {
          id: userIdBuffer,
          name: challengeData.user.name,
          displayName: challengeData.user.displayName
        },
        pubKeyCredParams: challengeData.pubKeyCredParams,
        timeout: challengeData.timeout || 60000,
        attestation: challengeData.attestation as AttestationConveyancePreference,
        authenticatorSelection: {
          ...challengeData.authenticatorSelection,
          // Configure for better Windows Hello compatibility
          authenticatorAttachment: undefined, // Allow both platform and cross-platform authenticators
          userVerification: 'preferred' as UserVerificationRequirement, // Prefer user verification but don't require it
          requireResidentKey: false // Don't require resident key for better compatibility
        }
      }
    };
  }

  /**
   * Convert server authentication options to WebAuthn format
   */
  private convertAuthenticationOptions(serverOptions: any): CredentialRequestOptions {
    if (!serverOptions) {
      throw new Error('Invalid server options: missing options object');
    }

    // Handle nested challenge structure
    let challengeData = serverOptions;
    if (serverOptions.challenge && typeof serverOptions.challenge === 'object') {
      challengeData = serverOptions.challenge;
    }

    if (!challengeData.challenge) {
      throw new Error('Invalid server options: missing challenge');
    }
    
    return {
      publicKey: {
        challenge: this.base64ToArrayBuffer(challengeData.challenge),
        timeout: challengeData.timeout || 60000,
        rpId: challengeData.rpId,
        allowCredentials: challengeData.allowCredentials?.map((cred: any) => ({
          type: cred.type as PublicKeyCredentialType,
          id: this.base64ToArrayBuffer(cred.id),
          transports: cred.transports
        })),
        userVerification: challengeData.userVerification as UserVerificationRequirement
      }
    };
  }

  /**
   * Create WebAuthn credential
   */
  private async createCredential(options: CredentialCreationOptions): Promise<PublicKeyCredential> {
    try {
      console.log('[PasskeyService] Starting WebAuthn credential creation...');
      console.log('[PasskeyService] WebAuthn options:', options);
      
      if (options.publicKey) {
        console.log('[PasskeyService] Challenge type:', typeof options.publicKey.challenge);
        console.log('[PasskeyService] Challenge constructor:', options.publicKey.challenge.constructor.name);
        console.log('[PasskeyService] Challenge byteLength:', options.publicKey.challenge.byteLength);
        console.log('[PasskeyService] User ID type:', typeof options.publicKey.user.id);
        console.log('[PasskeyService] User ID constructor:', options.publicKey.user.id.constructor.name);
        console.log('[PasskeyService] User ID byteLength:', options.publicKey.user.id.byteLength);
      } else {
        console.error('[PasskeyService] No publicKey in options!');
      }
      
      // Check browser compatibility
      console.log('[PasskeyService] Browser compatibility check:');
      console.log('[PasskeyService] - navigator.credentials:', !!navigator.credentials);
      console.log('[PasskeyService] - navigator.credentials.create:', !!navigator.credentials?.create);
      console.log('[PasskeyService] - PublicKeyCredential:', !!window.PublicKeyCredential);
      console.log('[PasskeyService] - isUserVerifyingPlatformAuthenticatorAvailable:', !!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable);
      
      // Check if platform authenticator is available
      if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
        try {
          const isAvailable = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          console.log('[PasskeyService] - Platform authenticator available:', isAvailable);
          if (!isAvailable) {
            console.warn('[PasskeyService] - Platform authenticator (Windows Hello) not available');
          }
        } catch (error) {
          console.log('[PasskeyService] - Error checking platform authenticator:', error);
        }
      }
      
      // Check for Windows Hello specifically
      const isWindows = navigator.userAgent.includes('Windows');
      const isEdge = navigator.userAgent.includes('Edg');
      const isChrome = navigator.userAgent.includes('Chrome');
      console.log('[PasskeyService] - Browser detection:', { isWindows, isEdge, isChrome });
      
      if (isWindows && (isEdge || isChrome)) {
        console.log('[PasskeyService] - Windows browser detected, checking Windows Hello availability...');
      }
      
      // Check if the options meet the authenticator selection requirements
      if (options.publicKey?.authenticatorSelection?.authenticatorAttachment === 'platform') {
        console.log('[PasskeyService] - Requiring platform authenticator (Touch ID, Face ID, Windows Hello)');
      }
      
      // Check if we're on HTTPS
      console.log('[PasskeyService] - Current protocol:', window.location.protocol);
      console.log('[PasskeyService] - Is HTTPS:', window.location.protocol === 'https:');
      
      console.log('[PasskeyService] Calling navigator.credentials.create()...');
      
      // Ensure this is called in response to a user gesture
      if (!window.event || !window.event.isTrusted) {
        console.warn('[PasskeyService] WebAuthn call may not be in response to a user gesture');
      }
      
      // Add a small delay to ensure the user gesture is properly registered
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('[PasskeyService] About to call navigator.credentials.create() with options:', {
        publicKey: {
          challenge: options.publicKey?.challenge ? `ArrayBuffer(${options.publicKey.challenge.byteLength} bytes)` : 'undefined',
          rp: options.publicKey?.rp,
          user: options.publicKey?.user ? {
            id: `ArrayBuffer(${options.publicKey.user.id.byteLength} bytes)`,
            name: options.publicKey.user.name,
            displayName: options.publicKey.user.displayName
          } : 'undefined',
          pubKeyCredParams: options.publicKey?.pubKeyCredParams,
          timeout: options.publicKey?.timeout,
          attestation: options.publicKey?.attestation,
          authenticatorSelection: options.publicKey?.authenticatorSelection
        }
      });
      
      // Add a timeout to detect hanging - increased for Windows Hello
      const createPromise = navigator.credentials.create(options) as Promise<PublicKeyCredential>;
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('WebAuthn create() timed out after 30 seconds - Windows Hello may have completed but the response was not received. Please try again.')), 30000)
      );
      
      let credential: PublicKeyCredential;
      try {
        credential = await Promise.race([createPromise, timeoutPromise]);
      } catch (error: any) {
        // If the first attempt fails on Windows, try with more permissive options
        const isWindows = navigator.userAgent.includes('Windows');
        if (isWindows && (error?.name === 'NotAllowedError' || error?.name === 'NotSupportedError')) {
          console.log('[PasskeyService] First attempt failed on Windows, trying with more permissive options...');
          
          // Create more permissive options for Windows Hello
          const permissiveOptions: CredentialCreationOptions = {
            publicKey: {
              challenge: options.publicKey!.challenge,
              rp: options.publicKey!.rp,
              user: options.publicKey!.user,
              pubKeyCredParams: options.publicKey!.pubKeyCredParams,
              timeout: options.publicKey!.timeout,
              attestation: options.publicKey!.attestation,
              authenticatorSelection: {
                authenticatorAttachment: undefined, // Allow any authenticator
                userVerification: 'discouraged' as UserVerificationRequirement, // Don't require user verification
                requireResidentKey: false
              }
            }
          };
          
          console.log('[PasskeyService] Retrying with permissive options...');
          const retryPromise = navigator.credentials.create(permissiveOptions) as Promise<PublicKeyCredential>;
          const retryTimeoutPromise = new Promise<never>((_, reject) => 
            setTimeout(() => reject(new Error('WebAuthn retry timed out after 30 seconds')), 30000)
          );
          
          credential = await Promise.race([retryPromise, retryTimeoutPromise]);
        } else {
          throw error;
        }
      }
      console.log('[PasskeyService] navigator.credentials.create() completed successfully');
      
      console.log('[PasskeyService] WebAuthn credential created successfully:', credential);
      console.log('[PasskeyService] Credential type:', credential?.type);
      console.log('[PasskeyService] Credential id length:', credential?.id?.length);
      console.log('[PasskeyService] Credential rawId length:', credential?.rawId?.byteLength);
      console.log('[PasskeyService] Credential response type:', credential?.response?.constructor?.name);
      
      if (!credential) {
        throw new Error('Failed to create credential');
      }
      return credential;
    } catch (error: any) {
      console.error('[PasskeyService] WebAuthn create credential error:', error);
      console.error('[PasskeyService] Error name:', error?.name);
      console.error('[PasskeyService] Error message:', error?.message);
      console.error('[PasskeyService] Error stack:', error?.stack);
      
      // Provide more specific error messages based on error type
      if (error?.name === 'NotAllowedError') {
        const isWindows = navigator.userAgent.includes('Windows');
        if (isWindows) {
          throw new Error('Windows Hello was cancelled or not available. Please ensure Windows Hello is set up and try again.');
        } else {
          throw new Error('Passkey creation was cancelled or not allowed by the user');
        }
      } else if (error?.name === 'NotSupportedError') {
        const isWindows = navigator.userAgent.includes('Windows');
        if (isWindows) {
          throw new Error('Windows Hello is not supported or not set up on this device. Please set up Windows Hello in Windows Settings.');
        } else {
          throw new Error('Passkey creation is not supported on this device or browser');
        }
      } else if (error?.name === 'SecurityError') {
        throw new Error('Passkey could not be created. Try again, or use another sign-in method.');
      } else if (error?.name === 'InvalidStateError') {
        throw new Error('A passkey for this account may already exist on this device.');
      } else if (error?.name === 'ConstraintError') {
        throw new Error('This device cannot create a passkey. Try another device or sign-in method.');
      } else if (error?.name === 'UnknownError') {
        throw new Error('Passkey could not be created. Please try again.');
      } else if (error?.message?.includes('timed out')) {
        throw new Error('Passkey creation timed out. Approve the device prompt and try again.');
      }
      
      throw error;
    }
  }

  /**
   * Get WebAuthn credential
   */
  private async getCredential(options: CredentialRequestOptions): Promise<PublicKeyCredential> {
    try {
      const credential = await navigator.credentials.get(options) as PublicKeyCredential;
      if (!credential) {
        throw new Error('Failed to get credential');
      }
      return credential;
    } catch (error) {
      console.error('WebAuthn get credential error:', error);
      // Re-throw enriched error so higher layers can show a helpful message
      const mapped = this.mapAuthenticationError(error);
      throw mapped;
    }
  }

  /**
   * Convert WebAuthn credential to server format
   */
  private convertCredentialForServer(credential: PublicKeyCredential): any {
    console.log('[PasskeyService] Converting credential for server...');
    console.log('[PasskeyService] Credential type:', credential.type);
    console.log('[PasskeyService] Credential id:', credential.id);
    console.log('[PasskeyService] Credential rawId length:', credential.rawId.byteLength);
    console.log('[PasskeyService] Response type:', credential.response.constructor.name);
    
    const response = credential.response as AuthenticatorAttestationResponse | AuthenticatorAssertionResponse;
    
    const convertedCredential = {
      id: credential.id,
      type: credential.type,
      rawId: this.arrayBufferToBase64(credential.rawId),
      response: {
        clientDataJSON: this.arrayBufferToBase64(response.clientDataJSON),
        ...(response instanceof AuthenticatorAttestationResponse && {
          attestationObject: this.arrayBufferToBase64(response.attestationObject)
        }),
        ...(response instanceof AuthenticatorAssertionResponse && {
          authenticatorData: this.arrayBufferToBase64(response.authenticatorData),
          signature: this.arrayBufferToBase64(response.signature),
          userHandle: response.userHandle ? this.arrayBufferToBase64(response.userHandle) : null
        })
      }
    };
    
    console.log('[PasskeyService] Converted credential structure:', {
      id: convertedCredential.id,
      type: convertedCredential.type,
      rawId: convertedCredential.rawId.substring(0, 20) + '...',
      response: {
        clientDataJSON: convertedCredential.response.clientDataJSON.substring(0, 50) + '...',
        ...(convertedCredential.response.attestationObject && {
          attestationObject: convertedCredential.response.attestationObject.substring(0, 50) + '...'
        }),
        ...(convertedCredential.response.authenticatorData && {
          authenticatorData: convertedCredential.response.authenticatorData.substring(0, 50) + '...'
        }),
        ...(convertedCredential.response.signature && {
          signature: convertedCredential.response.signature.substring(0, 50) + '...'
        })
      }
    });
    
    return convertedCredential;
  }

  /**
   * Convert base64 string to ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    try {
      console.log('[PasskeyService] Converting base64 to ArrayBuffer:', base64);
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      console.log('[PasskeyService] ArrayBuffer created successfully, length:', bytes.buffer.byteLength);
      return bytes.buffer;
    } catch (error) {
      console.error('[PasskeyService] Error converting base64 to ArrayBuffer:', error);
      console.error('[PasskeyService] Base64 string:', base64);
      throw new Error(`Failed to convert base64 to ArrayBuffer: ${error}`);
    }
  }

  /**
   * Convert ArrayBuffer to base64 string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Map raw WebAuthn errors to user-friendly messages for sign-in flow
   */
  private mapAuthenticationError(err: any): Error {
    const name = (err && err.name) || '';
    const message = (err && err.message) || '';

    // Browser/OS level
    if (name === 'NotAllowedError' || message.toLowerCase().includes('not allowed') || message.toLowerCase().includes('timed out')) {
      const isWindows = navigator.userAgent.includes('Windows');
      return new Error(
        isWindows
          ? 'Windows Hello was cancelled or timed out. Try again, or sign in with email.'
          : 'Passkey was cancelled or timed out. Try again, or sign in with email.'
      );
    }
    if (name === 'AbortError') {
      return new Error('Passkey sign-in was cancelled. Try again when you are ready.');
    }
    if (name === 'SecurityError') {
      return new Error('Passkey sign-in could not be completed. Try again, or sign in with email.');
    }
    if (name === 'UnknownError' || message === 'Unknown error') {
      return new Error(
        'This passkey is not available on this site. Sign in with email, or use the device where you created it.'
      );
    }
    if (name === 'InvalidStateError') {
      return new Error('No passkey found on this device. Register one first, or sign in with email.');
    }
    if (name === 'NotSupportedError') {
      const isWindows = navigator.userAgent.includes('Windows');
      return new Error(
        isWindows
          ? 'Windows Hello is not available on this device. Sign in with email instead.'
          : 'Passkeys are not supported on this device. Sign in with email instead.'
      );
    }

    // Network/backend envelope
    const httpMsg = (err && err.error && (err.error.message || err.error.error || err.error)) || undefined;
    if (typeof httpMsg === 'string' && httpMsg.length > 0) {
      return new Error(httpMsg);
    }
    if (message && message.toLowerCase().includes('invalid server options')) {
      return new Error('Passkey sign-in is temporarily unavailable. Please try again later.');
    }

    return new Error(message || 'Passkey sign-in failed. Try again, or sign in with email.');
  }
}
